import { useQuery } from "@tanstack/react-query";
import type { VipAccessStatus } from "@/lib/vip-access";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

type AppRole = Database["public"]["Enums"]["app_role"];

export function useUserRoles() {
  const [roles, setRoles] = useState<AppRole[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function fetchRoles() {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData.session?.user.id;
      if (!userId) {
        if (!cancelled) {
          setRoles(null);
          setLoading(false);
        }
        return;
      }

      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId);

      if (!cancelled) {
        if (error) {
          setRoles(null);
        } else {
          setRoles((data ?? []).map((r) => r.role));
        }
        setLoading(false);
      }
    }

    fetchRoles();

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session?.user) {
        setRoles(null);
        setLoading(false);
      } else {
        fetchRoles();
      }
    });

    return () => {
      cancelled = true;
      listener.subscription.unsubscribe();
    };
  }, []);

  return { roles, loading };
}

// UI cache only: protected requests always check the database again.
export function useIsVip() {
  const [userId, setUserId] = useState<string | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  useEffect(() => {
    let active = true;
    let authChanged = false;
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      authChanged = true;
      if (active) {
        setUserId(session?.user.id ?? null);
        setAuthLoading(false);
      }
    });
    void supabase.auth
      .getSession()
      .then(({ data }) => {
        if (active && !authChanged) {
          setUserId(data.session?.user.id ?? null);
          setAuthLoading(false);
        }
      })
      .catch(() => {
        if (active) {
          setUserId(null);
          setAuthLoading(false);
        }
      });
    return () => {
      active = false;
      listener.subscription.unsubscribe();
    };
  }, []);
  const q = useQuery({
    queryKey: ["vip-access", userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("consultar_acceso_vip", { p_user_id: userId! });
      if (error) throw error;
      return data as VipAccessStatus;
    },
    staleTime: 0,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    retry: 1,
  });
  return {
    isVip: !!userId && !q.isError && !!q.data?.puede_acceder,
    vipActivo: !!userId && !q.isError && !!q.data?.vip_activo,
    accesoStaff: !!userId && !q.isError && !!q.data?.acceso_staff,
    loading: authLoading || (!!userId && q.isPending),
  };
}
